import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { MeResponse, UpdateMeBody, UserDTO } from "@zplanem/responses"
import type { Locale } from "@zplanem/translations"
import type React from "react"
import { createContext, useContext, useEffect } from "react"
import { apiFetch } from "../lib/api-client"
import { queryKeys } from "../lib/query-keys"
import { useAuth } from "./auth-context"
import { useLocale } from "./locale-context"

interface UserContextType {
	profile: UserDTO | null
	patchProfile: (body: UpdateMeBody) => Promise<UserDTO>
	deleteAccount: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { user } = useAuth()
	const { locale, setLocale } = useLocale()
	const queryClient = useQueryClient()

	const meQuery = useQuery({
		queryKey: queryKeys.me(),
		queryFn: () =>
			apiFetch<MeResponse>("/api/common/users/me", { method: "GET" }),
		enabled: !!user,
	})

	const { mutate: patchMe, mutateAsync: patchMeAsync } = useMutation({
		mutationFn: (body: UpdateMeBody) =>
			apiFetch<MeResponse>("/api/common/users/me", {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
		onSuccess: (res) => {
			const previous = queryClient.getQueryData<MeResponse>(queryKeys.me())
			const previousMainRole = previous?.user.mainRole
			queryClient.setQueryData(queryKeys.me(), res)
			if (previousMainRole && previousMainRole !== res.user.mainRole) {
				queryClient.removeQueries({
					predicate: (q) => q.queryKey[0] === "properties",
				})
			}
		},
	})

	const { mutateAsync: deleteMeAsync } = useMutation({
		mutationFn: () =>
			apiFetch<null>("/api/common/users/me", {
				method: "DELETE",
			}),
	})

	// Sign-out: drop any cached /me data so the next login starts clean.
	useEffect(() => {
		if (!user) queryClient.removeQueries({ queryKey: queryKeys.me() })
	}, [user, queryClient])

	// First-login locale reconciliation. The server-side /me returns
	// `language: null` for a brand-new user — write the browser locale into
	// their profile. For returning users, mirror the DB value back into the
	// locale context so the UI matches the user's persisted preference.
	const meUser = meQuery.data?.user
	useEffect(() => {
		if (!meUser) return
		if (meUser.language === null) {
			patchMe({ language: locale })
		} else if (meUser.language !== locale) {
			setLocale(meUser.language as Locale)
		}
	}, [meUser, locale, setLocale, patchMe])

	const patchProfile = async (body: UpdateMeBody): Promise<UserDTO> => {
		const res = await patchMeAsync(body)
		if (res.user.language && res.user.language !== locale) {
			setLocale(res.user.language as Locale)
		}
		return res.user
	}

	const deleteAccount = async (): Promise<void> => {
		await deleteMeAsync()
		queryClient.clear()
	}

	return (
		<UserContext.Provider
			value={{ profile: meUser ?? null, patchProfile, deleteAccount }}
		>
			{children}
		</UserContext.Provider>
	)
}

export const useUser = () => {
	const context = useContext(UserContext)
	if (context === undefined) {
		throw new Error("useUser must be used within a UserProvider")
	}
	return context
}

/**
 * Narrowed variant of [useUser] for callers that always run inside the
 * authenticated/onboarded shell. App.tsx already holds the loading
 * screen until `profile !== null`, so the runtime assertion below never
 * fires in practice — it exists so the type can be narrowed and so a
 * future caller mounted outside the shell crashes early instead of
 * sprinkling `?.` checks back into the tree.
 */
export function useAuthenticatedUser(): {
	profile: UserDTO
	patchProfile: (body: UpdateMeBody) => Promise<UserDTO>
	deleteAccount: () => Promise<void>
} {
	const { profile, patchProfile, deleteAccount } = useUser()
	if (!profile) {
		throw new Error(
			"useAuthenticatedUser called before the profile was loaded — " +
				"this hook is only valid inside the authenticated/onboarded shell.",
		)
	}
	return { profile, patchProfile, deleteAccount }
}
